/**
 * Project View Page
 * @implements US-013 - Project Creation (Frontend)
 * @implements US-014 - Project Listing & Navigation
 * @implements US-015 - Project Editing (Settings link)
 * @implements US-016 - Project Archival
 * @implements US-017 - Project Deletion
 * @implements US-020 - AI PM Conversation UI
 * @implements US-021 - Automatic Conversation Initiation
 * @implements US-029 - Basic Specification Viewing (Specifications navigation)
 * @implements US-037 - Basic Roadmap Viewing (Roadmap navigation)
 * @implements FS-002.3.2 - Project Navigation
 * @implements FS-002.5 - Project Lifecycle States
 * @implements FS-002.7 - AI PM Conversation
 * @implements FS-002.7.1 - Automatic Conversation Initiation
 * @implements TS-065 - Archive/Unarchive UI
 * @implements TS-070 - Delete Project UI
 * @implements TS-087 - Post-creation redirect to conversation
 * @implements TS-088 - Onboarding flow restrictions (first project)
 *
 * Displays project details and the AI PM conversation interface.
 * For first-time users (onboarding), blocks navigation until one exchange
 * is completed with the AI PM.
 *
 * Route: /projects/:id
 */

import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  CircularProgress,
  Container,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  ArrowBack as ArrowBackIcon,
  Description as DescriptionIcon,
  FlagCircle as RoadmapIcon,
  NavigateNext as NavigateNextIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { useQuery, gql } from '@apollo/client';
import {
  LifecycleStateBadge,
  ControlStateBadge,
  ControlStateActions,
  LifecycleStateActions,
  LifecycleState,
  ControlState,
} from '@/components/project';
import { ConversationPanel, OnboardingBanner } from '@/components/conversation';
import { useProjectControlState, useOnboarding } from '@/hooks';

/**
 * GraphQL query for fetching a single project
 * @implements FS-002.3.2 - Project Navigation
 * @implements US-014 - Project Listing & Navigation
 *
 * Note: This is defined inline until codegen is run.
 * After running `yarn codegen`, use the generated useGetProjectQuery hook.
 */
const GET_PROJECT_QUERY = gql`
  query GetProject($id: ID!) {
    project(id: $id) {
      id
      title
      description
      lifecycleState
      controlState
      pmValidated
      poValidated
      createdAt
    }
  }
`;

/**
 * Project type from the GraphQL response
 */
interface Project {
  id: string;
  title: string;
  description: string | null;
  lifecycleState: LifecycleState;
  controlState: ControlState;
  pmValidated: boolean;
  poValidated: boolean;
  createdAt: string;
}

/**
 * Response type for GetProject query
 */
interface GetProjectResponse {
  project: Project | null;
}

/**
 * Variables for GetProject query
 */
interface GetProjectVariables {
  id: string;
}

/**
 * ProjectViewPage - Displays project details and AI PM conversation placeholder
 *
 * @implements US-013 - Project Creation (Frontend)
 * @implements US-014 - Project Listing & Navigation
 * @implements FS-002.3.2 - Project Navigation
 *
 * Features:
 * - Fetches project data using GetProject query
 * - Displays title, description, and state badges
 * - Handles loading, error, and 404 states
 * - Provides navigation back to projects list
 *
 * Route: /projects/:id
 */
export const ProjectViewPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // Fetch project data
  const { data, loading, error } = useQuery<
    GetProjectResponse,
    GetProjectVariables
  >(GET_PROJECT_QUERY, {
    variables: { id: id! },
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });

  const project = data?.project;

  // Initialize control state hook (must be called unconditionally)
  // @implements TS-074 - Control State Ravioli Container
  // @implements TS-075 - Control State UI Components
  const controlState = useProjectControlState({
    projectId: project?.id ?? '',
    pmValidated: project?.pmValidated ?? true,
    poValidated: project?.poValidated ?? true,
    controlState: (project?.controlState ?? 'NORMAL') as ControlState,
  });

  // Initialize onboarding hook for first-time users
  // @implements TS-088 - Onboarding flow restrictions
  const onboarding = useOnboarding({
    projectId: id ?? '',
  });

  // Render loading state
  if (loading && !data) {
    return (
      <Container maxWidth="md">
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  // Render error state
  if (error) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            Failed to load project: {error.message}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/projects')}
          >
            Back to Projects
          </Button>
        </Box>
      </Container>
    );
  }

  // Render 404 state (project not found)
  if (!project) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 4 }}>
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="h5" gutterBottom>
              Project Not Found
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              The project you are looking for does not exist or you do not have
              access to it.
            </Typography>
            <Button
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/projects')}
            >
              Back to Projects
            </Button>
          </Paper>
        </Box>
      </Container>
    );
  }

  // Format created date
  const createdDate = new Date(project.createdAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 3 }}
        >
          <Link
            component={RouterLink}
            to="/projects"
            color="inherit"
            underline="hover"
          >
            Projects
          </Link>
          <Typography color="text.primary">{project.title}</Typography>
        </Breadcrumbs>

        {/* Project Header */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          {/* Title, State Badges, and Settings */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              mb: 2,
            }}
          >
            <Typography variant="h4" component="h1">
              {project.title}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <LifecycleStateBadge state={project.lifecycleState} />
              <ControlStateBadge state={project.controlState} />
              {/* Specifications Button - @implements US-029 - Basic Specification Viewing */}
              <Button
                component={RouterLink}
                to={`/projects/${project.id}/specifications`}
                variant="outlined"
                size="small"
                startIcon={<DescriptionIcon />}
                sx={{ ml: 1 }}
              >
                Specifications
              </Button>
              {/* Roadmap Button - @implements US-037 - Basic Roadmap Viewing */}
              <Button
                component={RouterLink}
                to={`/projects/${project.id}/roadmap`}
                variant="outlined"
                size="small"
                startIcon={<RoadmapIcon />}
              >
                Roadmap
              </Button>
              {/* Settings Button - @implements US-015 - Project Editing */}
              <Button
                component={RouterLink}
                to={`/projects/${project.id}/settings`}
                variant="outlined"
                size="small"
                startIcon={<SettingsIcon />}
              >
                Settings
              </Button>
            </Stack>
          </Box>

          {/* Description */}
          {project.description && (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {project.description}
            </Typography>
          )}

          {/* Created date */}
          <Typography variant="body2" color="text.disabled">
            Created on {createdDate}
          </Typography>

          {/* Control State Actions - @implements TS-075 */}
          {/* Only show control state actions for ACTIVE projects */}
          {/* Note: Backend returns lowercase lifecycle states */}
          {project.lifecycleState?.toUpperCase() === 'ACTIVE' && (
            <Box
              sx={{
                mt: 3,
                pt: 2,
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Typography
                variant="subtitle2"
                color="text.secondary"
                sx={{ mb: 1 }}
              >
                Control State Actions
              </Typography>
              <ControlStateActions
                controlState={controlState}
                variant="inline"
                size="small"
                showIcons
              />
            </Box>
          )}

          {/* Lifecycle State Actions - @implements TS-065 */}
          {/* Archive/Unarchive buttons based on lifecycle state */}
          <Box
            sx={{
              mt: 3,
              pt: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{ mb: 1 }}
            >
              {project.lifecycleState?.toUpperCase() === 'ARCHIVED'
                ? 'Archived Project Actions'
                : 'Lifecycle Actions'}
            </Typography>
            {project.lifecycleState?.toUpperCase() === 'ARCHIVED' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                This project is archived and read-only. Unarchive to make
                changes.
              </Alert>
            )}
            <LifecycleStateActions
              projectId={project.id}
              projectTitle={project.title}
              lifecycleState={project.lifecycleState}
              controlState={project.controlState}
              size="small"
              showIcons
              onDeleted={() => navigate('/projects')}
            />
          </Box>
        </Paper>

        {/* Onboarding Banner - show during first project onboarding */}
        {/* @implements TS-088 - Onboarding flow restrictions */}
        {onboarding.isOnboarding && (
          <OnboardingBanner
            hasSentMessage={onboarding.hasSentMessage}
            hasReceivedResponse={onboarding.hasReceivedResponse}
            hasCompletedExchange={onboarding.hasCompletedExchange}
          />
        )}

        {/* AI PM Conversation Panel */}
        {/* @implements FS-002.7 - AI PM Conversation */}
        {/* @implements US-020 - AI PM Conversation UI */}
        {/* @implements TS-088 - Onboarding flow (callbacks for exchange tracking) */}
        <ConversationPanel
          projectId={project.id}
          isProjectArchived={
            project.lifecycleState?.toUpperCase() === 'ARCHIVED'
          }
          height="calc(100vh - 500px)"
          onMessageSent={onboarding.markMessageSent}
          onAiPmResponse={onboarding.markResponseReceived}
        />

        {/* Back to Projects Link */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/projects')}
          >
            Back to Projects
          </Button>
        </Box>
      </Box>
    </Container>
  );
};
